import { Color } from 'three'
import { gsap } from 'gsap'

import { deepMerge, getTargetOffset, setElementHide } from 'js/utils/Utils'

import BasePlaneMesh from 'js/webgl/customMeshes/base/BasePlaneMesh'
import Common from 'js/_core/Common'

import VertexShader from 'js/webgl/_glsl/vert/listThumbAMesh.vert'
import FragmentShader from 'js/webgl/_glsl/frag/listThumbAMesh.frag'

const defaults = {
  instanceName: 'listThumbAPlaneMesh',
  $names: {
    icon: '.home_business_sect_sub_panel_thumb_icon',
    text: '.c-news_panel_txts',
  },
  effectInfo: {
    bgColor1: '#eaeaea',
    bgColor2: '#b5b6c0',
  },
}

export default class ListThumbAPlaneMesh extends BasePlaneMesh {
  isInView = false
  inViewAnimated = false
  width = 0
  height = 0

  originPos = {
    x: 0,
    y: 0,
  }
  meshOffset = {
    x: 0,
    y: 0,
  }

  $a = null
  $icon = null
  $text = null

  constructor(options = {}) {
    super({
      vs: VertexShader,
      fs: FragmentShader,
    })
    this.webgl = null
    this.uniforms = this.uniforms || {}

    deepMerge(this, defaults, options)
  }

  init() {
    this._set$()
    super.init()
    this._setUniforms()
    this._setBaseSize()
    this.setResolutions()
    setElementHide(this.$target)
    this.setTexture()

    Common.webgl.stage.scene.add(this.mesh)

    this._addIoms()
    this._addEventListeners()
  }

  destroy() {
    this._removeIoms()
    this._removeEventListeners()

    if (this.material.uniforms.uTexture && this.material.uniforms.uTexture.value) {
      this.material.uniforms.uTexture.value.dispose()
      this.material.uniforms.uTexture.value = null
    }

    if (this.mesh.material && this.mesh.material.uniforms.uTexture && this.mesh.material.uniforms.uTexture.value) {
      this.mesh.material.uniforms.uTexture.value.dispose()
      this.mesh.material.uniforms.uTexture.value = null
    }

    if (this.uniforms.uTexture && this.uniforms.uTexture.value) {
      this.uniforms.uTexture.value.dispose()
      this.uniforms.uTexture.value = null
    }

    super.destroy()
  }

  _set$() {
    if (this.$target) {
      this.$a = this.$target.closest('a')
      if (this.$a) {
        this.$icon = this.$a.querySelector(this.$names.icon)
        this.$text = this.$a.querySelector(this.$names.text)
      }
    }
  }

  _addIoms() {
    if (Common.iom) {
      Common.iom.add(this.$target, () => {
        if (!this.isInView) {
          this.isInView = true
          Common.iom.remove(this.$target)
        }
      })
    }
  }

  _removeIoms() {
    if (Common.iom) {
      Common.iom.remove(this.$target)
    }
  }

  _addEventListeners() {
    if (this.$a) {
      if (Common.useMouseHover) {
        this.$a.addEventListener('mouseenter', this._onMouseEnter, false)
        this.$a.addEventListener('mouseleave', this._onMouseLeave, false)
      }
    }
  }

  _removeEventListeners() {
    if (this.$a) {
      if (Common.useMouseHover) {
        this.$a.removeEventListener('mouseenter', this._onMouseEnter)
        this.$a.removeEventListener('mouseleave', this._onMouseLeave)
      }
    }
  }

  _setUniforms() {
    deepMerge(this.uniforms, {
      uMeshOffset: {
        value: {
          x: 0,
          y: 0,
        },
      },
      uTexture: { value: null },
      uTextureResolution: {
        value: {
          x: 1,
          y: 1,
        },
      },
      uBgColor: { value: new Color(this.effectInfo.bgColor1).convertLinearToSRGB() },
      uBgColor2: { value: new Color(this.effectInfo.bgColor2).convertLinearToSRGB() },
      uOpacity: { value: 0 },
      uScaleInView: { value: 1 },
      uScaleMouseEnter: { value: 0 },
    })
  }

  setTexture() {
    const texturePath = this.$target.dataset['glImg']

    const textureList = Common.textureList['listThumbA']
    const listLength = textureList.length

    for (let i = 0; i < listLength; i++) {
      const texture = textureList[i]
      if (texture.name === texturePath) {
        if (texturePath === texture.name) {
          let width = 1
          let height = 1

          if (texture.image) {
            width = texture.image.naturalWidth
            height = texture.image.naturalHeight
          }

          this.uniforms.uTextureResolution.value.x = width
          this.uniforms.uTextureResolution.value.y = height
          requestAnimationFrame(() => {
            this.uniforms.uTexture.value = texture
          })
        }
        break
      }
    }
  }

  setResolutions() {
    if (this.$target) {
      const rect = this.$target.getBoundingClientRect()
      this.width = rect.width
      this.height = rect.height

      this.uniforms.uMeshResolution.value.x = this.width
      this.uniforms.uMeshResolution.value.y = this.height

      this.originPos.x = -(Common.webgl.stage.stageSize.width * 0.5) + this.width * 0.5
      this.originPos.y = Common.webgl.stage.stageSize.height * 0.5 - this.height * 0.5

      const offset = getTargetOffset(this.$target)
      this.meshOffset.x = offset.left
      this.meshOffset.y = offset.top

      this.mesh.scale.z = 0.0001
    }
  }

  _setPosition() {
    this.uniforms.uMeshOffset.value.x = this.originPos.x + this.meshOffset.x
    this.uniforms.uMeshOffset.value.y = this.originPos.y - (this.meshOffset.y - Common.scroll.currentScroll)
  }

  _setBaseSize() {
    super._setBaseSize()
    // this.wh = window.innerHeight
  }

  inViewAnimation() {
    requestAnimationFrame(() => {
      gsap.to(this.uniforms.uScaleInView, {
        value: 0,
        duration: 1.5,
        ease: 'quart.out',
      })

      gsap.to(this.uniforms.uOpacity, {
        value: 1,
        duration: 0.5,
        ease: 'quad.inOut',
      })
    })
  }
  async _enterAnimation() {
    await new Promise((resolve) => {
      requestAnimationFrame(() => {
        gsap.killTweensOf(this.uniforms.uScaleMouseEnter)
        gsap.to(this.uniforms.uScaleMouseEnter, {
          value: 1,
          duration: 0.65,
          ease: 'quart.out',
        })

        if (this.$icon) {
          gsap.killTweensOf(this.$icon)
          gsap.to(this.$icon, {
            color: '#0d0d0d',
            backgroundColor: '#ceff00',
            duration: 0.325,
            // ease: 'none',
          })
        }

        if (this.$text) {
          gsap.killTweensOf(this.$text)
          gsap.to(this.$text, {
            opacity: 0.5,
            duration: 0.5,
            ease: 'expo.out',
            // ease: 'none',
          })
        }

        gsap.delayedCall(0.2, () => {
          resolve()
        })
      })
    })
  }

  async _leaveAnimation() {
    await new Promise((resolve) => {
      requestAnimationFrame(() => {
        gsap.killTweensOf(this.uniforms.uScaleMouseEnter)
        gsap.to(this.uniforms.uScaleMouseEnter, {
          value: 0,
          duration: 0.6,
          ease: 'cubic.out',
          delay: 0.05,
        })

        if (this.$icon) {
          gsap.killTweensOf(this.$icon)
          gsap.to(this.$icon, {
            color: '#ffffff',
            backgroundColor: '#2c2c2c',
            duration: 0.25,
            ease: 'none',
            delay: 0.05,
          })
        }

        if (this.$text) {
          gsap.killTweensOf(this.$text)
          gsap.to(this.$text, {
            opacity: 1,
            duration: 0.25,
            ease: 'none',
            delay: 0.05,
          })
        }

        resolve()
      })
    })
  }

  _onMouseEnter = () => {
    this.isMouseEnter = true

    if (!this.isMouseEnterAnimating) {
      this._startMouseEnter()
    }
  }

  _onMouseLeave = () => {
    this.isMouseEnter = false
    if (!this.isMouseEnterAnimating) {
      this._startMouseLeave()
    }
  }

  onResize() {
    this._setBaseSize()
    this.setResolutions()
    this._setPosition()
  }

  onRaf() {
    this._setPosition()
    if (Common.iomEnable && this.isInView && !this.inViewAnimated) {
      this.inViewAnimated = true
      this.inViewAnimation()
    }
  }
}